import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleTable, StatusBadge } from '@src/components/ui';
import AdminLayout from './AdminLayout';
import ActionMenu from '../../components/ActionMenu';
import { formatDateForTable, formatPhoneForTable, getStatusBadgeColors, formatStatusLabel } from './utils/formatters';
import { loadClinics, updateClinic, getOrders } from '../../api';
import styles from './AdminDashboard.module.scss';
const AdminDashboard = () => {
    const navigate = useNavigate();
    const [dashboardStats, setDashboardStats] = useState({
        ordersThisWeek: 0,
        revenueThisWeek: 0,
        avgOrderValue: 0,
        onTimeShipping: 0
    });
    const [recentOrders, setRecentOrders] = useState([]);
    const [topClinics, setTopClinics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [clinicStatusFilter, setClinicStatusFilter] = useState('');
    const [orderStatusFilter, setOrderStatusFilter] = useState('');
    useEffect(() => {
        fetchDashboardData();
    }, []);
    const fetchDashboardData = async () => {
        try {
            const ordersResponse = await getOrders();
            const orders = ordersResponse.body?.results || ordersResponse.body || [];
            const now = new Date();
            const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            const ordersThisWeek = orders.filter((order) => {
                const orderDate = new Date(order.createdAt || order.created_at);
                return orderDate >= oneWeekAgo;
            });
            const ordersThisWeekCount = ordersThisWeek.length;
            const revenueThisWeek = ordersThisWeek.reduce((sum, order) => sum + (parseFloat(order.totalAmount || order.total || order.total_amount || 0) || 0), 0);
            const avgOrderValue = ordersThisWeekCount > 0 ? revenueThisWeek / ordersThisWeekCount : 0;
            const deliveredOrders = orders.filter((order) => order.status === 'delivered').length;
            const totalOrders = orders.length;
            const onTimePercentage = totalOrders > 0 ? Math.round((deliveredOrders / totalOrders) * 100) : 0;
            setDashboardStats({
                ordersThisWeek: ordersThisWeekCount,
                revenueThisWeek,
                avgOrderValue,
                onTimeShipping: onTimePercentage
            });
            const ordersWithClinicInfo = orders.map((order) => ({
                ...order,
                order_number: order.orderNumber || order.order_number,
                total_amount: order.totalAmount || order.total || order.total_amount,
                created_at: order.createdAt || order.created_at,
                payment_status: order.paymentStatus || order.payment_status,
                clinic_name: order.clinicName || order.clinic?.name || order.clinic_name || 'Unknown',
                clinic_email: order.clinicEmail || order.clinic?.email || order.clinic_email || order.email || 'N/A',
                contact_email: order.contactEmail || order.contact_email,
            }));
            const sortedOrders = [...ordersWithClinicInfo].sort((a, b) => {
                const dateA = new Date(a.createdAt || a.created_at || a.orderDate || a.order_date);
                const dateB = new Date(b.createdAt || b.created_at || b.orderDate || b.order_date);
                return dateB.getTime() - dateA.getTime();
            });
            setRecentOrders(sortedOrders.slice(0, 5));
            const clinicsResponse = await loadClinics(true);
            const clinics = clinicsResponse.body || [];
            const clinicsWithCapitalizedStatus = clinics.map((clinic) => ({
                ...clinic,
                approvalStatus: clinic.approvalStatus || clinic.approval_status
                    ? (clinic.approvalStatus || clinic.approval_status).charAt(0).toUpperCase() +
                        (clinic.approvalStatus || clinic.approval_status).slice(1).toLowerCase()
                    : 'Pending',
            }));
            const sortedClinics = [...clinicsWithCapitalizedStatus].sort((a, b) => new Date(b.createdAt || b.created_at).getTime() - new Date(a.createdAt || a.created_at).getTime());
            setTopClinics(sortedClinics.slice(0, 5));
        }
        catch (error) {
            console.error('Error fetching dashboard data:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const handleSetActive = async (clinic) => {
        setLoading(true);
        try {
            await updateClinic(clinic.id, { approval_status: 'approved' });
            await fetchDashboardData();
        }
        catch (err) {
            console.error('Error setting clinic to active:', err);
            alert('Failed to set clinic to active');
        }
        setLoading(false);
    };
    const handleDelete = async (clinic) => {
        if (window.confirm(`Are you sure you want to delete ${clinic.name}?`)) {
            setLoading(true);
            try {
                await updateClinic(clinic.id, { is_active: false });
                await fetchDashboardData();
            }
            catch (err) {
                console.error('Error deleting clinic:', err);
                alert('Failed to delete clinic');
            }
            setLoading(false);
        }
    };
    const handleSetRejected = async (clinic) => {
        const reason = window.prompt('Please provide a reason for rejecting this clinic:');
        if (!reason || !reason.trim()) {
            return;
        }
        setLoading(true);
        try {
            await updateClinic(clinic.id, { approval_status: 'rejected', rejection_reason: reason.trim() });
            await fetchDashboardData();
        }
        catch (err) {
            console.error('Error rejecting clinic:', err);
            alert('Failed to reject clinic');
        }
        setLoading(false);
    };
    const handleView = (clinic) => {
        navigate(`/web-admin/clinics/${clinic.id}`);
    };
    const handleOrderView = (order) => {
        navigate(`/web-admin/orders/${order.uuid || order.id}`);
    };
    const handleOrderDelete = async (order) => {
        if (window.confirm(`Are you sure you want to delete order ${order.order_number}?`)) {
            setLoading(true);
            try {
                const response = await fetch(`/api/v1/orders/${order.uuid || order.id}/`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                    },
                });
                if (!response.ok) {
                    throw new Error('Failed to delete order');
                }
                await fetchDashboardData();
            }
            catch (error) {
                console.error('Error deleting order:', error);
                alert('Failed to delete order');
            }
            setLoading(false);
        }
    };
    const handleUpdateStatus = async (order, newStatus) => {
        setLoading(true);
        try {
            const response = await fetch(`/api/v1/orders/${order.uuid || order.id}/`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
                },
                body: JSON.stringify({ status: newStatus }),
            });
            if (!response.ok) {
                throw new Error('Failed to update order status');
            }
            await fetchDashboardData();
        }
        catch (error) {
            console.error('Error updating order status:', error);
            alert('Failed to update order status');
        }
        setLoading(false);
    };
    const recentOrderColumns = [
        {
            key: 'order_number',
            header: 'Order #',
            render: (order) => (_jsx("span", { style: { color: '#078ff0', cursor: 'pointer' }, onClick: () => navigate(`/web-admin/orders/${order.uuid || order.id}`), children: order.orderNumber || order.order_number || 'N/A' }))
        },
        {
            key: 'order_placed',
            header: 'Order Placed',
            render: (order) => formatDateForTable(order.createdAt || order.created_at || order.orderDate || order.order_date),
            sortable: true,
        },
        {
            key: 'total_value',
            header: 'Total Value',
            render: (order) => `$${parseFloat(order.totalAmount || order.total || order.total_amount || 0).toFixed(2)}`,
        },
        {
            key: 'clinic_name',
            header: 'Clinic Name',
            render: (order) => order.clinicName || order.clinic_name || 'Unknown',
        },
        {
            key: 'contact',
            header: 'Contact',
            render: (order) => (_jsx("span", { style: { color: '#0066cc' }, children: order.clinicEmail || order.clinic_email || order.contactEmail || order.contact_email || 'N/A' })),
        },
        {
            key: 'payment_status',
            header: 'Payment',
            render: (order) => {
                const paymentStatus = order.paymentStatus || order.payment_status || 'pending';
                const paymentColorMap = {
                    pending: { background: '#FFF4CC', text: '#F5A623' },
                    processing: { background: '#E5F3FF', text: '#0066CC' },
                    completed: { background: '#E2FADE', text: '#42BA2D' },
                    failed: { background: '#FFE5E5', text: '#D32F2F' },
                    refunded: { background: '#F0F0F0', text: '#666666' },
                };
                const colors = paymentColorMap[paymentStatus.toLowerCase()] || { background: '#FFFFFF', text: '#2A2E2E' };
                return (_jsx(StatusBadge, { label: paymentStatus, bgColor: colors.background, color: colors.text }));
            },
        },
        {
            key: 'status',
            header: 'Status',
            accessor: 'status',
            render: (order) => {
                const status = order.status || 'pending';
                const statusColorMap = {
                    pending: { background: '#FFF4CC', text: '#F5A623' },
                    processing: { background: '#E5F3FF', text: '#0066CC' },
                    shipped: { background: '#B6EEFF', text: '#009ED4' },
                    delivered: { background: '#E2FADE', text: '#42BA2D' },
                    cancelled: { background: '#FFE5E5', text: '#D32F2F' },
                    failed_payment: { background: '#FFE5E5', text: '#D32F2F' },
                };
                const colors = statusColorMap[status.toLowerCase()] || { background: '#FFFFFF', text: '#2A2E2E' };
                return (_jsx(StatusBadge, { label: status, bgColor: colors.background, color: colors.text }));
            },
        },
        {
            key: 'actions',
            header: 'Action',
            render: (order) => (_jsx("div", { onClick: (e) => e.stopPropagation(), children: _jsx(ActionMenu, { options: [
                        { label: 'View Details', onClick: () => handleOrderView(order) },
                        { label: 'Delete', onClick: () => handleOrderDelete(order), color: 'red' },
                    ] }) })),
        },
    ];
    const topClinicsColumns = [
        {
            key: 'name',
            header: 'Clinic Name',
            render: (clinic) => clinic.name || 'N/A',
        },
        {
            key: 'website',
            header: 'Website',
            render: (clinic) => (_jsx("span", { style: { color: '#0066cc', cursor: 'pointer' }, onClick: () => navigate(`/web-admin/clinics/${clinic.id}`), children: clinic.website || 'N/A' })),
        },
        {
            key: 'date_registered',
            header: 'Date Registered',
            render: (clinic) => (_jsx("span", { style: { cursor: 'pointer' }, onClick: () => navigate(`/web-admin/clinics/${clinic.id}`), children: formatDateForTable(clinic.createdAt || clinic.created_at || clinic.date_registered) })),
            sortable: true,
        },
        {
            key: 'phone',
            header: 'Clinic Phone',
            render: (clinic) => (_jsx("span", { style: { color: '#0066cc', cursor: 'pointer' }, onClick: () => navigate(`/web-admin/clinics/${clinic.id}`), children: formatPhoneForTable(clinic) })),
        },
        {
            key: 'contact',
            header: 'Contact',
            render: (clinic) => (_jsx("span", { style: { color: '#0066cc', cursor: 'pointer' }, onClick: () => navigate(`/web-admin/clinics/${clinic.id}`), children: clinic.email || clinic.contact_name || clinic.contact_email || 'N/A' })),
        },
        {
            key: 'status',
            header: 'Status',
            accessor: 'approvalStatus',
            render: (clinic) => {
                const status = clinic.approvalStatus || clinic.approval_status || 'pending';
                const { background, text } = getStatusBadgeColors(status);
                return (_jsx(StatusBadge, { label: formatStatusLabel(status), bgColor: background, color: text }));
            },
        },
        {
            key: 'actions',
            header: 'Action',
            render: (clinic) => {
                return (_jsx("div", { onClick: (e) => e.stopPropagation(), children: _jsx(ActionMenu, { options: [
                            { label: 'View Details', onClick: () => handleView(clinic) },
                            { label: 'Approve', onClick: () => handleSetActive(clinic) },
                            { label: 'Reject', onClick: () => handleSetRejected(clinic) },
                        ] }) }));
            },
        },
    ];
    const handleClinicFilterChange = (value) => {
        setClinicStatusFilter(value);
    };
    const handleOrderFilterChange = (value) => {
        setOrderStatusFilter(value);
    };
    const filteredClinics = clinicStatusFilter
        ? topClinics.filter((clinic) => {
            const status = clinic.approvalStatus || clinic.approval_status || 'pending';
            const normalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            if (normalizedStatus === 'Approved' && clinicStatusFilter === 'Active')
                return true;
            if (clinicStatusFilter === 'Inactive' && clinic.is_active === false)
                return true;
            return normalizedStatus === clinicStatusFilter;
        })
        : topClinics;
    const filteredOrders = orderStatusFilter
        ? recentOrders.filter((order) => {
            const status = order.status || 'submitted';
            const normalizedStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
            return normalizedStatus === orderStatusFilter;
        })
        : recentOrders;
    return (_jsx(AdminLayout, { children: _jsxs("div", { className: styles.container, children: [_jsxs("div", { className: styles.statsRow, children: [_jsxs("div", { className: `${styles.statBox} ${styles.green}`, children: [_jsx("div", { className: styles.statNumber, children: dashboardStats.ordersThisWeek }), _jsx("div", { className: styles.statLabel, children: "Orders This Week" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.blue}`, children: [_jsxs("div", { className: styles.statNumber, children: ["$", dashboardStats.revenueThisWeek.toLocaleString()] }), _jsx("div", { className: styles.statLabel, children: "Revenue This Week" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.brown}`, children: [_jsxs("div", { className: styles.statNumber, children: ["$", dashboardStats.avgOrderValue.toFixed(2)] }), _jsx("div", { className: styles.statLabel, children: "Avg Order Value" })] }), _jsxs("div", { className: `${styles.statBox} ${styles.yellow}`, children: [_jsxs("div", { className: styles.statNumber, children: [dashboardStats.onTimeShipping, "%"] }), _jsx("div", { className: styles.statLabel, children: "On-Time Shipping" })] })] }), _jsxs("div", { className: styles.tablesContainer, children: [_jsx("div", { className: styles.tableSection, children: loading ? (_jsx("div", { children: "Loading..." })) : (_jsxs(_Fragment, { children: [_jsx(SimpleTable, { columns: topClinicsColumns, data: filteredClinics, rowHeight: "60px", tableHeading: "New Clinics", titleStyle: {
                                            color: '#000',
                                            fontFamily: 'Nunito',
                                            fontSize: '24px',
                                            fontStyle: 'normal',
                                            fontWeight: 600,
                                            lineHeight: '28px',
                                        }, hideTopBorder: true, enableSelection: true, filterColumn: "status", filterLabel: "Show All Statuses", onFilterChange: handleClinicFilterChange, onRowClick: (clinic) => navigate(`/web-admin/clinics/${clinic.id}`), bulkActions: [
                                            {
                                                label: 'Approve',
                                                action: async (selectedRows) => {
                                                    setLoading(true);
                                                    try {
                                                        const updatePromises = selectedRows.map((clinic) => updateClinic(clinic.id, { approval_status: 'approved' }));
                                                        await Promise.all(updatePromises);
                                                        await fetchDashboardData();
                                                    }
                                                    catch (err) {
                                                        alert('Failed to approve clinics');
                                                    }
                                                    setLoading(false);
                                                }
                                            },
                                            {
                                                label: 'Reject',
                                                action: async (selectedRows) => {
                                                    const reason = window.prompt('Please provide a reason for rejecting these clinics:');
                                                    if (!reason || !reason.trim()) {
                                                        return;
                                                    }
                                                    setLoading(true);
                                                    try {
                                                        const updatePromises = selectedRows.map((clinic) => updateClinic(clinic.id, { approval_status: 'rejected', rejection_reason: reason.trim() }));
                                                        await Promise.all(updatePromises);
                                                        await fetchDashboardData();
                                                    }
                                                    catch (err) {
                                                        alert('Failed to reject clinics');
                                                    }
                                                    setLoading(false);
                                                }
                                            },
                                            {
                                                label: 'Delete',
                                                action: async (selectedRows) => {
                                                    if (!confirm(`Are you sure you want to delete ${selectedRows.length} clinics?`)) {
                                                        return;
                                                    }
                                                    setLoading(true);
                                                    try {
                                                        const updatePromises = selectedRows.map((clinic) => updateClinic(clinic.id, { is_active: false }));
                                                        await Promise.all(updatePromises);
                                                        await fetchDashboardData();
                                                    }
                                                    catch (err) {
                                                        alert('Failed to delete clinics');
                                                    }
                                                    setLoading(false);
                                                }
                                            }
                                        ] }), _jsx("div", { className: styles.viewAllLink, children: _jsx("a", { href: "#", onClick: (e) => {
                                                e.preventDefault();
                                                navigate('/web-admin/clinics');
                                            }, children: "View All Clinics" }) })] })) }), _jsx("div", { className: styles.tableSection, children: loading ? (_jsx("div", { children: "Loading..." })) : (_jsxs(_Fragment, { children: [_jsx(SimpleTable, { columns: recentOrderColumns, data: filteredOrders, rowHeight: "60px", tableHeading: "Recent Orders", titleStyle: {
                                            color: '#000',
                                            fontFamily: 'Nunito',
                                            fontSize: '24px',
                                            fontStyle: 'normal',
                                            fontWeight: 600,
                                            lineHeight: '28px',
                                        }, hideTopBorder: true, filterColumn: "status", filterLabel: "Show All Statuses", onFilterChange: handleOrderFilterChange, onRowClick: (order) => navigate(`/web-admin/orders/${order.uuid || order.id}`) }), _jsx("div", { className: styles.viewAllLink, children: _jsx("a", { href: "#", onClick: (e) => {
                                                e.preventDefault();
                                                navigate('/web-admin/orders');
                                            }, children: "View All Orders" }) })] })) })] })] }) }));
};
export default AdminDashboard;
